<template>
  <div>
    <section class="section-padding bg-white">
      <div class="container-width">
        <div class="text-center mb-16">
          <h1 class="text-dark-900 mb-4">Portfolio</h1>
          <p class="text-xl text-dark-600 max-w-3xl mx-auto">
            Ausgewählte Projekte und Case Studies
          </p>
        </div>

        <!-- Filter -->
        <div class="flex flex-wrap gap-3 justify-center mb-12">
          <button
            v-for="category in categories"
            :key="category"
            @click="selectedCategory = category"
            class="px-6 py-2 rounded-full transition-colors"
            :class="selectedCategory === category
              ? 'bg-primary-500 text-white'
              : 'bg-gray-100 text-dark-700 hover:bg-gray-200'"
          >
            {{ category }}
          </button>
        </div>

        <!-- Projekte Grid -->
        <div class="grid md:grid-cols-2 gap-8">
          <div
            v-for="project in filteredProjects"
            :key="project.title"
            class="bg-gray-50 rounded-lg overflow-hidden hover:shadow-xl transition-shadow"
          >
            <!-- Vorschaubild -->
            <div class="h-64 overflow-hidden">
              <NuxtImg
                v-if="project.image"
                :src="project.image"
                :alt="project.title"
                class="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                loading="lazy"
                format="webp"
                quality="85"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center"
                :style="{ background: project.placeholderColor }"
              >
                <span class="text-4xl font-bold text-white/30 select-none">{{ project.initials }}</span>
              </div>
            </div>
            <div class="p-6">
              <div class="flex gap-2 mb-3">
                <span
                  v-for="tag in project.tags"
                  :key="tag"
                  class="text-xs px-3 py-1 bg-primary-100 text-primary-700 rounded-full"
                >
                  {{ tag }}
                </span>
              </div>
              <h3 class="text-2xl font-bold text-dark-900 mb-2">{{ project.title }}</h3>
              <p class="text-dark-600 mb-4">{{ project.description }}</p>
              <NuxtLink
                :to="`/portfolio/${project.slug}`"
                class="text-primary-500 hover:text-primary-600 font-medium inline-flex items-center gap-2"
              >
                Case Study ansehen
                <span>→</span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Labs & Experimente -->
    <section class="section-padding bg-gray-50 border-t border-gray-200">
      <div class="container-width">

        <!-- Header -->
        <div class="mb-12">
          <span class="text-xs font-semibold tracking-widest text-primary-500 uppercase">Coding</span>
          <h2 class="text-dark-900 mt-2 mb-4">Labs & Experimente</h2>
          <p class="text-lg text-dark-600 max-w-2xl">
            Kleine Coding-Experimente und Prototypen – kein Anspruch auf Vollständigkeit,
            aber ein Blick auf meine technische Neugier jenseits von Figma.
          </p>
        </div>

        <!-- Experiments Grid -->
        <div class="grid md:grid-cols-2 gap-8">
          <div
            v-for="experiment in experiments"
            :key="experiment.id"
            class="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow"
          >
            <!-- CodePen Embed -->
            <div class="relative w-full" style="padding-top: 56.25%">
              <iframe
                :src="`https://codepen.io/eltuctuc/embed/${experiment.id}?default-tab=result&theme-id=light&editable=true`"
                class="absolute inset-0 w-full h-full"
                loading="lazy"
                allowfullscreen
                title="CodePen Experiment"
              />
            </div>

            <!-- Info -->
            <div class="p-5">
              <h3 class="text-lg font-semibold text-dark-900 mb-1">{{ experiment.title }}</h3>
              <p v-if="experiment.description" class="text-sm text-dark-500 mb-4">
                {{ experiment.description }}
              </p>
              <a
                :href="`https://codepen.io/eltuctuc/pen/${experiment.id}`"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-primary-500 hover:text-primary-600 font-medium inline-flex items-center gap-1"
              >
                Auf CodePen ansehen
                <span>↗</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
useHead({
  title: 'Portfolio - Case Studies & Projekte | Enrico Reinsdorf',
  meta: [
    { name: 'description', content: 'Ausgewählte Projekte und Case Studies: BRZ 365 Design System, OK.PERS+, ERGO Direkt. UX Design für Enterprise Software und digitale Produkte.' },
    { name: 'keywords', content: 'UX Portfolio, Case Studies, Design System, BRZ 365, OK.PERS+, ERGO Direkt' }
  ]
})

const categories = ref(['Alle', 'UX Design', 'Design System', 'User Research', 'Product Owner'])
const selectedCategory = ref('Alle')

const projects = [
  {
    title: 'BAUER+KIRCH – Digitale Strategie & Online Bibliothek',
    slug: 'bauer-kirch-digitale-strategie',
    description: 'Digitale Strategie mit 10-Jahres-Roadmap und MMP-Konzept „Online Bibliothek" für einen Weltmarktführer in der Industrieanlagen-Branche. Bewerbungs-Case-Study für BAUER+KIRCH.',
    tags: ['UX Design', 'User Research'],
    category: ['UX Design', 'User Research'],
    image: '/images/bauer-kirch/online-bibliothek.png',
    initials: 'B+K',
    placeholderColor: 'linear-gradient(135deg, #1A1A1A 0%, #3a3a3a 100%)'
  },
  {
    title: 'ZEP Zeiterfassung – UX Redesign',
    slug: 'zep-zeiterfassung-redesign',
    description: 'Redesign der Zeiterfassungsmaske und Navigation von ZEP. Bewerbungs-Case-Study mit Heuristic Review, User Research und drei Layoutkonzepten bis zum Hi-Fi-Prototyp.',
    tags: ['UX Design', 'User Research'],
    category: ['UX Design', 'User Research'],
    image: '/images/zep/01-projektzeiten.png',
    initials: 'ZEP',
    placeholderColor: 'linear-gradient(135deg, #1A1A1A 0%, #3a3a3a 100%)'
  },
  {
    title: 'BRZ 365 Design System',
    slug: 'brz-365-design-system',
    description: 'Entwicklung eines umfassenden Design Systems für die BRZ 365 Plattform.',
    tags: ['Design System', 'UX Design'],
    category: ['Design System', 'UX Design'],
    image: null,
    initials: 'BRZ',
    placeholderColor: 'linear-gradient(135deg, #FF6B35 0%, #e55a24 100%)'
  },
  {
    title: 'OK.PERS+',
    slug: 'ok-pers-plus',
    description: 'UX-Optimierung und User Research für die Personalverwaltungssoftware.',
    tags: ['User Research', 'UX Design'],
    category: ['User Research', 'UX Design'],
    image: null,
    initials: 'OK+',
    placeholderColor: 'linear-gradient(135deg, #1A1A1A 0%, #FF6B35 100%)'
  },
  {
    title: 'ERGO Direkt',
    slug: 'ergo-direkt',
    description: 'Produktentwicklung und UX-Design für digitale Versicherungslösungen.',
    tags: ['Product Owner', 'UX Design'],
    category: ['Product Owner', 'UX Design'],
    image: null,
    initials: 'ED',
    placeholderColor: 'linear-gradient(135deg, #2d4a8a 0%, #1a2d54 100%)'
  },
  {
    title: 'FIEGE myFIEGE – Mitarbeitervernetzung',
    slug: 'fiege-mitarbeitervernetzung',
    description: 'Assessment Center Aufgabe bei FIEGE Logistik: Feature-Konzept für die myFIEGE-Plattform zur Verbesserung der Mitarbeitervernetzung – mit Barrier-Analyse, Gamification-System und smarten Vernetzungsvorschlägen.',
    tags: ['UX Design', 'User Research'],
    category: ['UX Design', 'User Research'],
    image: '/images/fiege/ac-praesentation-1.png',
    initials: 'FG',
    placeholderColor: 'linear-gradient(135deg, #1A1A1A 0%, #3a3a3a 100%)'
  },
  {
    title: 'Debeka – UX-Berater Probeaufgaben',
    slug: 'debeka-ux-berater-probeaufgaben',
    description: 'Bewerbungs-Probeaufgabe bei Debeka (7 Arbeitstage): UX-Theorie, Anforderungsanalyse aus Kontextinterview (22+ Nutzungsanforderungen), Expertenevaluation des AR-Tarifrechners mit Redesign-Konzepten sowie praxisnahe UX-Beratungsszenarien.',
    tags: ['UX Design', 'User Research'],
    category: ['UX Design', 'User Research'],
    image: '/images/debeka/analyse-1.png',
    initials: 'DK',
    placeholderColor: 'linear-gradient(135deg, #1A1A1A 0%, #3a3a3a 100%)'
  },
  {
    title: 'Anwalt.de – SnackEase App Konzept',
    slug: 'anwalt-de-snack-kiosk',
    description: 'Bewerbungs-Assessment bei Anwalt.de: Konzeption der mobilen App "SnackEase" für den Mitarbeiter-Snack-Kiosk – mit Stakeholder-Analyse, 3 Personas, 14-Screen-Informationsarchitektur, 1-Touch-Kaufflow und Gamification-Leaderboard.',
    tags: ['UX Design', 'User Research'],
    category: ['UX Design', 'User Research'],
    image: '/images/anwalt-de/hifi/product-overview.png',
    initials: 'AW',
    placeholderColor: 'linear-gradient(135deg, #1A1A1A 0%, #3a3a3a 100%)'
  }
]

const experiments = [
  {
    id: 'abOVNZX',
    title: 'Experiment',
    description: ''
  }
]

const filteredProjects = computed(() => {
  if (selectedCategory.value === 'Alle') {
    return projects
  }
  return projects.filter(p => p.category.includes(selectedCategory.value))
})
</script>
